import React, { Dispatch, MutableRefObject, ReactElement, SetStateAction, useEffect, useState } from 'react'
// Externals imports.
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
// Sub imports.
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core'
import { ExpansionPanelItem } from './Types'
// CSS imports. Commented out because of current build process.
// import './expansionPanel.scss';

/**
 * Component properties type.
 */
type Props = {
    expansionPanelItems: ExpansionPanelItem[]
    mainMenuElem?: MutableRefObject<HTMLElement | null>
    menuItemCount?: number
    setMainMenuStyles?: Dispatch<SetStateAction<{ overflow: string }>>
    onAnimationEnd?: () => void
}

/**
 * Expansion panel component.
 *
 * @param expansionPanelItems List of expansion panel items.
 * @param mainMenuElem Main menu container reference object.
 * @param menuItemCount The menu item count.
 * @param setMainMenuStyles Setting method to define styling for main menu container.
 */
const ExpansionPanel: React.FC<Props> = ({
    expansionPanelItems,
    mainMenuElem,
    menuItemCount,
    setMainMenuStyles,
    onAnimationEnd
}: Props): ReactElement => {
    const handleChange =
        (expansionPanelitem: ExpansionPanelItem): ((event: React.ChangeEvent<unknown>, newExpanded: boolean) => void) =>
        (event: React.ChangeEvent<unknown>, newExpanded: boolean): void => {
            if (expansionPanelitem.onChange) {
                expansionPanelitem.onChange(newExpanded)
            }
        }

    // The minimum height the details section can be.
    const MIN_MENU_HEIGHT = 150
    // This is hard coded as there is a restriction on 'useRef' inside a looped
    // component therefore we are unable to reference each menu item generated in the loop.
    const MENU_ITEM_HEIGHT = 62

    // This is the react styling object for all accordion content sections in the main menu.
    const [accordionMenuItemStyle, setAccordionMenuItemStyle] = useState({})

    const sizeMenuElements = (): void => {
        const menuHeight = mainMenuElem?.current?.getBoundingClientRect().height

        const menuItemsHeight = menuItemCount ? menuItemCount * MENU_ITEM_HEIGHT : 0

        let maxDetailsHeight = menuHeight ? menuHeight - menuItemsHeight : MIN_MENU_HEIGHT

        if (maxDetailsHeight < MIN_MENU_HEIGHT) {
            maxDetailsHeight = MIN_MENU_HEIGHT
        }

        const detailsStyle = {
            maxHeight: `${maxDetailsHeight}px`,
            overflow: 'auto',
            width: '100%'
        }

        if (setMainMenuStyles) {
            if (menuHeight && menuHeight < menuItemsHeight + maxDetailsHeight) {
                setMainMenuStyles({ overflow: 'auto' })
            } else {
                setMainMenuStyles({ overflow: 'hidden' })
            }
        }

        setAccordionMenuItemStyle(detailsStyle)
    }

    const deleteResources = (): void => {
        window.removeEventListener('resize', sizeMenuElements)
    }

    useEffect(() => {
        window.addEventListener('resize', sizeMenuElements)

        return deleteResources
    }, [sizeMenuElements])

    useEffect(() => {
        sizeMenuElements()
        // React renders the length of panels as 0 for the initial render.
        // Therefore we add it as a dependency in the useEffect array to check for change.
    }, [menuItemCount])

    const toExpansionItem = (item: ExpansionPanelItem, index: number): ReactElement => {
        const { title, content } = item

        return (
            <Accordion
                // eslint-disable-next-line @typescript-eslint/no-base-to-string
                key={`${title.toString()}-${index}`}
                expanded={item.isExpanded}
                onChange={handleChange(item)}
                onAnimationEnd={onAnimationEnd}
            >
                <AccordionSummary
                    className='summary'
                    expandIcon={<ExpandMoreIcon />}
                    IconButtonProps={{ disableRipple: true, disableFocusRipple: true }}
                    aria-controls='panel1a-content'
                    id='panel1a-header'
                >
                    {title}
                </AccordionSummary>
                <AccordionDetails>
                    <div style={accordionMenuItemStyle}>{content}</div>
                </AccordionDetails>
            </Accordion>
        )
    }

    return <div className='expansion-panel'>{expansionPanelItems.map(toExpansionItem)}</div>
}

export default ExpansionPanel
